import { Component, Input } from '@angular/core';
import { StorePublicReview, StoreRatingSummary } from '../../../service/store/store.service';
import { PLATFORM_NAMES_BY_KEY } from './store-reviews.platforms';

/**
 * What buyers on codigrate.com say about a theme (owner, 2026-10-04): the
 * average of every rating across its platforms, the selected platform's own
 * when it has some, and the approved comments. Presentational: the theme page
 * (through app-color-detail) passes the numbers in. These are the numbers the
 * page's structured data carries, so they have to be on the page.
 */
@Component( {
  selector: 'app-store-reviews',
  templateUrl: './store-reviews.component.html',
  styleUrls: [ './store-reviews.component.scss' ],
} )
export class StoreReviewsComponent {

  @Input() summary: StoreRatingSummary | null = null;
  /** the selected platform's own ratings, when it has any */
  @Input() platformSummary: StoreRatingSummary | null = null;
  @Input() platformName = '';
  @Input() reviews: StorePublicReview[] = [];
  /** what the count is of ("12 ratings from buyers on Codigrate"); the tool pages say their own */
  @Input() countLabel = 'from buyers on Codigrate';
  /** theme pages: the ratings on the other platforms, counted beside the buyers' (owner, 2026-10-04) */
  @Input() otherCount = 0;
  /** theme pages: the one rating over every platform (the banner's); else the summary's own average */
  @Input() average: number | null = null;

  get buyerCount(): number {
    return this.summary?.count || 0;
  }

  get shownAverage(): number {
    return this.average ?? this.summary?.average ?? 0;
  }

  /** one line each (owner, 2026-10-04): "1 rating from buyers on Codigrate", "12 ratings from other platforms" */
  get countLines(): string[] {
    const ratings = ( n: number ) => `${ n } ${ n === 1 ? 'rating' : 'ratings' }`;
    const parts: string[] = [];
    if ( this.buyerCount > 0 ) {
      parts.push( `${ ratings( this.buyerCount ) } ${ this.countLabel }` );
    }
    if ( this.otherCount > 0 ) {
      parts.push( `${ ratings( this.otherCount ) } from other platforms` );
    }
    return parts;
  }

  readonly stars = [ 1, 2, 3, 4, 5 ];
  /** comments shown before "Show All" */
  readonly firstShown = 4;
  expanded = false;

  get shownReviews(): StorePublicReview[] {
    return this.expanded ? this.reviews : this.reviews.slice( 0, this.firstShown );
  }

  /** how much of a star is filled (0..1) at position `star` for `value` */
  fill( star: number, value: number ): number {
    return Math.max( 0, Math.min( 1, value - ( star - 1 ) ) );
  }

  platformLabel( key: string ): string {
    return PLATFORM_NAMES_BY_KEY[ key ] || key;
  }

  formatDate( iso: string ): string {
    const d = new Date( iso + 'T00:00:00Z' );
    return isNaN( d.getTime() ) ? '' : d.toLocaleDateString( 'en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' } );
  }

  trackByReview( index: number, review: StorePublicReview ): string {
    return review.sku + review.date + index;
  }
}
